import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowDownUp, ArrowLeft, Bell, BellOff, Image as ImageIcon, Loader2, Plus, Search, Video } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/lib/api";
import { SPECIES } from "@/lib/market";
import { useI18n } from "@/context/I18nContext";

export function StatusBadge({ status, t, size = "sm" }) {
  if (status === "open") return null;
  const cls = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-sm px-3 py-1";
  return (
    <span className={`inline-flex items-center rounded-md bg-red-600 text-white font-extrabold uppercase tracking-wider shadow ${cls}`} data-testid="listing-status-badge">
      {t(`market.${status}`)}
    </span>
  );
}

function ListingRow({ l, t }) {
  const thumb = l.media.find((m) => m.kind === "image");
  const hasVideo = l.media.some((m) => m.kind === "video");
  return (
    <Link
      to={`/market/listing/${l.id}`}
      className={`flex gap-3 bg-white rounded-xl border border-app-border p-3 hover:border-olive transition-colors ${l.status !== "open" ? "opacity-80" : ""}`}
      data-testid={`listing-row-${l.id}`}
    >
      <div className="w-20 h-20 rounded-lg bg-cream-2 overflow-hidden shrink-0 flex items-center justify-center text-espresso/30">
        {thumb ? <img src={`${API}/market/media/${thumb.id}`} alt="" className="w-full h-full object-cover" loading="lazy" /> : hasVideo ? <Video className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <h3 className="font-semibold text-espresso truncate flex-1">{l.title}</h3>
          <StatusBadge status={l.status} t={t} />
        </div>
        <div className="text-[11px] text-espresso/60 mt-0.5 flex flex-wrap gap-x-2">
          <span className="font-semibold text-olive">{t(`market.species_${l.species}`)}</span>
          <span>{l.owner_name}</span>
          <span>{new Date(l.created_at).toLocaleDateString()}</span>
          {l.age_days >= 1 && <span>{t("market.age_days").replace("{n}", l.age_days)}</span>}
          <span>{l.media.length} {t("market.files")}</span>
          {l.replies?.length > 0 && <span>{l.replies.length} {t("market.replies")}</span>}
        </div>
        <p className="text-xs text-espresso/70 mt-1 line-clamp-2">{l.description}</p>
      </div>
    </Link>
  );
}

export default function MarketListPage() {
  const { type } = useParams();
  const { t } = useI18n();
  const [species, setSpecies] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("new");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [rows, setRows] = useState(null);
  const [follows, setFollows] = useState([]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    api.get("/market/follows").then(({ data }) => setFollows(data.species)).catch(() => {});
  }, []);

  useEffect(() => {
    if (type !== "sell" && type !== "buy") return;
    setRows(null);
    api.get(`/market/listings?type=${type}&species=${species}&status=${status}&sort=${sort}&q=${encodeURIComponent(debounced)}`).then(({ data }) => setRows(data.listings)).catch(() => setRows([]));
  }, [type, species, status, sort, debounced]);

  if (type !== "sell" && type !== "buy") return <Navigate to="/market" replace />;

  const followKey = species || "all";
  const following = follows.includes(followKey);
  const toggleFollow = async () => {
    const next = following ? follows.filter((s) => s !== followKey) : [...follows, followKey];
    const { data } = await api.put("/market/follows", { species: next });
    setFollows(data.species);
    toast.success(following ? t("market.unfollowed") : t("market.followed"));
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid={`market-${type}-page`}>
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/market" className="text-espresso/60 hover:text-espresso" data-testid="market-back"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /></Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-espresso flex-1">{t(type === "sell" ? "market.selling" : "market.buying")}</h1>
        <Button asChild data-testid="market-add-button">
          <Link to={`/market/${type}/new`}><Plus className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("market.add_bird")}</Link>
        </Button>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 rtl:left-auto rtl:right-3 text-espresso/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("market.search_placeholder")} className="pl-9 rtl:pl-3 rtl:pr-9" data-testid="market-search" />
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="species-filter">
        {["", ...SPECIES].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setSpecies(s)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${species === s ? "bg-olive text-white border-olive" : "bg-white text-espresso/70 border-app-border hover:border-olive"}`}
            data-testid={`species-${s || "all"}`}
          >
            {s ? t(`market.species_${s}`) : t("market.all_species")}
            {follows.includes(s || "all") && <Bell className="w-3 h-3 inline-block ml-1 rtl:ml-0 rtl:mr-1 -mt-0.5" />}
          </button>
        ))}
        <button
          type="button"
          onClick={toggleFollow}
          className={`ml-auto rtl:ml-0 rtl:mr-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${following ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white text-espresso/70 border-app-border hover:border-olive"}`}
          data-testid="follow-species-button"
        >
          {following ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
          {(following ? t("market.unfollow") : t("market.follow")).replace("{s}", species ? t(`market.species_${species}`) : t("market.all_species"))}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="status-filter">
        {[["", t("market.filter_all")], ["open", t("market.filter_pending")], ["done", t(type === "sell" ? "market.filter_sold" : "market.filter_bought")]].map(([k, label]) => (
          <button key={k || "all"} type="button" onClick={() => setStatus(k)} data-testid={`status-${k || "all"}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${status === k ? "bg-espresso text-white border-espresso" : "bg-white text-espresso/70 border-app-border hover:border-espresso"}`}>
            {label}
          </button>
        ))}
        <div className="ml-auto rtl:ml-0 rtl:mr-auto inline-flex rounded-full border border-app-border bg-white overflow-hidden text-xs font-semibold" data-testid="sort-toggle">
          {[["new", t("market.sort_new")], ["old", t("market.sort_old")]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setSort(k)} data-testid={`sort-${k}`}
              className={`inline-flex items-center gap-1 px-3 py-1 transition-colors ${sort === k ? "bg-olive text-white" : "text-espresso/70 hover:bg-cream-2"}`}>
              <ArrowDownUp className="w-3 h-3" />{label}
            </button>
          ))}
        </div>
      </div>
      {rows === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-espresso/50 bg-white rounded-xl border border-app-border p-6 text-center" data-testid="market-empty">{debounced || species || status ? t("market.no_results") : t("market.empty")}</div>
      ) : (
        <div className="space-y-2" data-testid="market-rows">{rows.map((l) => <ListingRow key={l.id} l={l} t={t} />)}</div>
      )}
    </div>
  );
}
